import { useEffect, useRef, useState } from 'react';

import type { Theme } from '@material-ui/core';
import { IconButton, Tooltip, makeStyles, useTheme } from '@material-ui/core';

import {
  RiCheckLine,
  RiFileCopyLine,
  RiFullscreenLine,
} from '@remixicon/react';
import type { Loader } from 'vega';
import type { Result } from 'vega-embed';

import { Text } from '@spotify-internal/encore-web';
import { getConfidenceDistinctCategoricalColors } from '@spotify/confidence-charts-react';
import {
  DialogCompositeMui as DialogComposite,
  useDisclosure,
} from '@spotify/confidence-core-react';

import { useChartCopy } from '../../hooks';
import { buildConfidenceVegaConfig } from './confidenceVegaTheme';
import {
  injectCompactAxisFormat,
  injectConfidenceSeriesStyles,
  injectTooltipInteractivity,
  normalizeDateOnlyTemporalAxes,
} from './vegaSpecUtils';

import styles from './ChartBlock.module.css';

const sandboxLoader: Loader = {
  load: () => Promise.reject(new Error('External data loading is disabled')),
  http: () => Promise.reject(new Error('External data loading is disabled')),
  file: () => Promise.reject(new Error('External data loading is disabled')),
  sanitize: (uri: string) => Promise.resolve({ href: uri, localFile: false }),
};

function stripDataUrls(spec: Record<string, unknown>) {
  const stripFromArray = (arr: unknown[]) => {
    for (const entry of arr) {
      if (typeof entry === 'object' && entry !== null && 'url' in entry) {
        delete (entry as Record<string, unknown>).url;
      }
    }
  };
  if (Array.isArray(spec.data)) {
    stripFromArray(spec.data);
  } else if (
    typeof spec.data === 'object' &&
    spec.data !== null &&
    'url' in spec.data
  ) {
    delete (spec.data as Record<string, unknown>).url;
  }
  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer === 'object' && layer !== null) {
        stripDataUrls(layer as Record<string, unknown>);
      }
    }
  }
}

function formatTooltip(
  value: Record<string, unknown>,
  valueToHtml: (value: unknown) => string,
): string {
  const seenEntries = new Set<string>();

  return Object.entries(value)
    .map(([key, val]) => [key, valueToHtml(val)] as const)
    .filter(([key, htmlValue]) => {
      const entryKey = `${key.toLowerCase()}\u0000${htmlValue}`;
      if (seenEntries.has(entryKey)) return false;

      seenEntries.add(entryKey);
      return true;
    })
    .map(
      ([key, htmlValue]) =>
        `<div style="margin-bottom:2px"><strong>${key}:</strong> ${htmlValue}</div>`,
    )
    .join('');
}

const DEFAULT_SPEC_WIDTH = 500;
const DEFAULT_SPEC_HEIGHT = 400;
const RESIZE_SETTLE_DELAY_MS = 100;

const getAvailableChartWidth = (
  containerWidth: number,
  horizontalPadding: number,
): number => Math.max(0, containerWidth - horizontalPadding);

const isMultiViewSpec = (spec: Record<string, unknown>): boolean => {
  if (
    spec.facet ||
    spec.repeat ||
    spec.concat ||
    spec.hconcat ||
    spec.vconcat
  ) {
    return true;
  }

  const encoding = spec.encoding;
  if (typeof encoding !== 'object' || encoding === null) {
    return false;
  }

  const encodingRecord = encoding as Record<string, unknown>;
  return Boolean(
    encodingRecord.facet || encodingRecord.column || encodingRecord.row,
  );
};

function applyResponsiveSize(
  spec: Record<string, unknown>,
  availableWidth: number,
  theme: Theme,
) {
  const isVegaLite =
    typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');

  if (isVegaLite && availableWidth > 0 && !isMultiViewSpec(spec)) {
    spec.width = availableWidth;
    return;
  }

  if (availableWidth <= 0) return;

  const specPadding = spec.padding;
  let hPadding = 0;
  if (typeof specPadding === 'number') {
    hPadding = specPadding * 2;
  } else if (typeof specPadding === 'object' && specPadding !== null) {
    const p = specPadding as Record<string, number>;
    hPadding = (p.left ?? 0) + (p.right ?? 0);
  }
  const totalWidth = ((spec.width as number) ?? DEFAULT_SPEC_WIDTH) + hPadding;
  if (totalWidth > availableWidth) {
    const scale = availableWidth / totalWidth;
    spec.width = Math.round(
      ((spec.width as number) ?? DEFAULT_SPEC_WIDTH) * scale,
    );
    spec.height = Math.round(
      ((spec.height as number) ?? DEFAULT_SPEC_HEIGHT) * scale,
    );
    if (typeof specPadding === 'number') {
      spec.padding = Math.round(specPadding * scale);
    } else if (typeof specPadding === 'object' && specPadding !== null) {
      const p = specPadding as Record<string, number>;
      spec.padding = {
        left: Math.round((p.left ?? 0) * scale),
        right: Math.round((p.right ?? 0) * scale),
        top: Math.round((p.top ?? 0) * scale),
        bottom: Math.round((p.bottom ?? 0) * scale),
      };
    }
  }

  // Inject text color for full Vega text marks
  const textColor = `var(--text-subdued, ${theme.palette.text.secondary})`;
  if (Array.isArray(spec.marks)) {
    for (const mark of spec.marks as Record<string, unknown>[]) {
      const encode = mark.encode as
        | Record<string, Record<string, unknown>>
        | undefined;
      if (mark.type === 'text' && encode?.enter) {
        encode.enter.fill = encode.enter.fill ?? { value: textColor };
      }
    }
  }
}

const useVegaTooltipStyles = makeStyles(theme => ({
  '@global': {
    '#vg-tooltip-element': {
      // vega-tooltip sets z-index: 1000 inline; MUI Dialog is 1300, so !important is needed to lift above the dialog overlay
      zIndex: `${theme.zIndex.tooltip} !important`,
    },
  },
}));

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    '&:hover $actionButtons, &:focus-within $actionButtons': {
      opacity: 1,
    },
  },
  chartContainer: {
    backgroundColor: `var(--background-elevated-base, ${theme.palette.background.paper})`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 0),
    overflow: 'hidden',
    textAlign: 'center',
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
  actionButtons: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(0.5),
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
  },
  actionButton: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dialogCopyButton: {
    position: 'absolute',
    right: 8 + 48,
    top: 8,
  },
  dialogBody: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  dialogChartContainer: {
    backgroundColor: `var(--background-elevated-base, ${theme.palette.background.paper})`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    height: '100%',
    padding: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
}));

type VegaChartProps = {
  content: string;
  className?: string;
  fit?: 'width' | 'container';
  onRenderErrorChange?: (error: string | null) => void;
  onRenderStateChange?: (isRendering: boolean) => void;
};

export const VegaChart = ({
  content,
  className,
  fit = 'width',
  onRenderErrorChange,
  onRenderStateChange,
}: VegaChartProps) => {
  const theme = useTheme();
  const chartHorizontalPadding = theme.spacing(4);
  useVegaTooltipStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialContainerSize, setInitialContainerSize] = useState({
    height: 0,
    width: 0,
  });
  const latestContainerSizeRef = useRef({ height: 0, width: 0 });
  const resultRef = useRef<Result | null>(null);
  const renderedContentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rootRef.current) return undefined;
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
    let fadeFrame: number | undefined;
    let resizeSnapshot: HTMLDivElement | null = null;
    let resizeSequence = 0;
    let hasMeasuredSize = false;
    let isActive = true;

    const clearResizeSnapshot = () => {
      cancelAnimationFrame(fadeFrame ?? 0);
      resizeSnapshot?.remove();
      resizeSnapshot = null;
    };

    const fadeResizeSnapshot = (snapshot: HTMLDivElement) => {
      const removeSnapshot = () => {
        if (resizeSnapshot === snapshot) resizeSnapshot = null;
        snapshot.remove();
      };
      const handleTransitionEnd = (event: TransitionEvent) => {
        if (event.target === snapshot && event.propertyName === 'opacity') {
          removeSnapshot();
        }
      };

      snapshot.addEventListener('transitionend', handleTransitionEnd);
      snapshot.dataset.fading = 'true';

      const transitionDurations = window
        .getComputedStyle(snapshot)
        .transitionDuration.split(',')
        .map(duration => Number.parseFloat(duration));
      if (transitionDurations.every(duration => duration === 0)) {
        removeSnapshot();
      }
    };

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;

      const nextSize = {
        height: entry.contentRect.height,
        width: entry.contentRect.width,
      };
      const currentSize = latestContainerSizeRef.current;
      if (
        nextSize.width === currentSize.width &&
        (fit === 'width' || nextSize.height === currentSize.height)
      ) {
        return;
      }

      latestContainerSizeRef.current = nextSize;
      if (!hasMeasuredSize) {
        hasMeasuredSize = true;
        setInitialContainerSize(nextSize);
        return;
      }

      const currentResizeSequence = ++resizeSequence;
      clearResizeSnapshot();
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(async () => {
        const latestContainerSize = latestContainerSizeRef.current;
        const availableWidth =
          fit === 'container'
            ? latestContainerSize.width
            : getAvailableChartWidth(
                latestContainerSize.width,
                chartHorizontalPadding,
              );
        const result = resultRef.current;
        const root = rootRef.current;
        const container = containerRef.current;

        if (result && root && container && availableWidth > 0) {
          const snapshot = document.createElement('div');
          snapshot.className = styles.resizeSnapshot;
          snapshot.setAttribute('aria-hidden', 'true');
          snapshot.setAttribute('data-testid', 'vega-chart-resize-snapshot');
          snapshot.appendChild(container.cloneNode(true));
          root.appendChild(snapshot);
          resizeSnapshot = snapshot;
          // Commit the visible clone before Vega updates the live SVG.
          void snapshot.offsetWidth;

          try {
            result.view.width(availableWidth);
            if (fit === 'container' && latestContainerSize.height > 0) {
              result.view.height(latestContainerSize.height);
            }
            await result.view.runAsync();
          } catch {
            // Keep the last successful chart visible when a layout-only resize fails.
          }

          if (isActive && currentResizeSequence === resizeSequence) {
            fadeFrame = requestAnimationFrame(() =>
              fadeResizeSnapshot(snapshot),
            );
          } else {
            clearResizeSnapshot();
          }
        }
      }, RESIZE_SETTLE_DELAY_MS);
    });
    observer.observe(rootRef.current);
    return () => {
      isActive = false;
      observer.disconnect();
      clearTimeout(resizeTimeout);
      clearResizeSnapshot();
    };
  }, [chartHorizontalPadding, fit]);

  useEffect(() => {
    const container = containerRef.current;
    if (
      !container ||
      !content ||
      initialContainerSize.width === 0 ||
      (fit === 'container' && initialContainerSize.height === 0)
    ) {
      return undefined;
    }

    let result: Result | null = null;
    let isActive = true;
    const shouldReportRendering = renderedContentRef.current !== content;

    if (shouldReportRendering) onRenderStateChange?.(true);

    const renderChart = async () => {
      try {
        const [{ default: vegaEmbed }] = await Promise.all([
          import('vega-embed'),
          document.fonts?.ready ?? Promise.resolve(),
        ]);
        const spec = JSON.parse(content);
        const containerSize = latestContainerSizeRef.current;
        const availableWidth =
          fit === 'container'
            ? containerSize.width
            : getAvailableChartWidth(
                containerSize.width,
                chartHorizontalPadding,
              );

        applyResponsiveSize(spec, availableWidth, theme);
        if (fit === 'container') {
          spec.width = containerSize.width;
          spec.height = containerSize.height;
          spec.autosize = {
            contains: 'padding',
            resize: true,
            type: 'fit',
          };
        }
        stripDataUrls(spec);

        const isVegaLite =
          typeof spec.$schema === 'string' &&
          spec.$schema.includes('vega-lite');
        if (isVegaLite) {
          injectConfidenceSeriesStyles(
            spec,
            getConfidenceDistinctCategoricalColors(
              theme.palette.type === 'dark' ? 'dark' : 'light',
            ),
          );
          injectCompactAxisFormat(spec);
          normalizeDateOnlyTemporalAxes(spec);
        }
        injectTooltipInteractivity(spec);

        const nextResult = await vegaEmbed(container, spec, {
          actions: false,
          renderer: 'svg',
          config: buildConfidenceVegaConfig(theme),
          loader: sandboxLoader,
          tooltip: { formatTooltip },
        });

        if (!isActive) {
          nextResult.finalize();
          return;
        }

        const latestContainerSize = latestContainerSizeRef.current;
        const latestAvailableWidth =
          fit === 'container'
            ? latestContainerSize.width
            : getAvailableChartWidth(
                latestContainerSize.width,
                chartHorizontalPadding,
              );
        if (
          latestAvailableWidth > 0 &&
          (latestAvailableWidth !== availableWidth ||
            (fit === 'container' &&
              latestContainerSize.height !== containerSize.height))
        ) {
          nextResult.view.width(latestAvailableWidth);
          if (fit === 'container' && latestContainerSize.height > 0) {
            nextResult.view.height(latestContainerSize.height);
          }
          await nextResult.view.runAsync();
        }
        if (!isActive) {
          nextResult.finalize();
          return;
        }

        result = nextResult;
        resultRef.current = nextResult;
        renderedContentRef.current = content;
        setError(null);
        onRenderErrorChange?.(null);
      } catch (e) {
        if (!isActive) return;

        const renderError =
          e instanceof Error ? e.message : 'Failed to render chart';
        setError(renderError);
        onRenderErrorChange?.(renderError);
      } finally {
        if (isActive && shouldReportRendering) onRenderStateChange?.(false);
      }
    };

    renderChart();
    return () => {
      isActive = false;
      if (resultRef.current === result) resultRef.current = null;
      result?.finalize();
    };
  }, [
    content,
    theme,
    chartHorizontalPadding,
    fit,
    initialContainerSize,
    onRenderErrorChange,
    onRenderStateChange,
  ]);

  if (error) return null;

  return (
    <div
      ref={rootRef}
      className={styles.resizeSurface}
      data-testid="vega-chart-resize-surface"
    >
      <div ref={containerRef} className={className} />
    </div>
  );
};

export type ChartBlockProps = {
  content: string;
};

const copyTooltipTitle = (status: 'idle' | 'success' | 'error') => {
  if (status === 'success') return 'Copied!';
  if (status === 'error') return 'Failed to copy';
  return 'Copy chart';
};

export const ChartBlock = ({ content }: ChartBlockProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const captureRef = useRef<HTMLDivElement>(null);
  const dialogCaptureRef = useRef<HTMLDivElement>(null);
  const [dialogOpen, dialog] = useDisclosure();
  const [renderError, setRenderError] = useState<string | null>(null);
  const backgroundColor = theme.palette.background.default;
  const inlineCopy = useChartCopy(backgroundColor);
  const dialogCopy = useChartCopy(backgroundColor);

  return (
    <>
      <div className={classes.root}>
        <div ref={captureRef}>
          {/* VegaChart renders nothing when the spec fails, so without
              reporting the error the chart area is silently blank. */}
          <VegaChart
            content={content}
            className={classes.chartContainer}
            onRenderErrorChange={setRenderError}
          />
        </div>
        {renderError && (
          <Text variant="bodySmall" semanticColor="textSubdued">
            This chart could not be rendered.
          </Text>
        )}
        <div className={classes.actionButtons}>
          <Tooltip title={copyTooltipTitle(inlineCopy.status)}>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={() =>
                inlineCopy.handleCopyToClipboard(captureRef.current)
              }
              aria-label="Copy chart to clipboard"
            >
              {inlineCopy.status === 'success' ? (
                <RiCheckLine size={18} />
              ) : (
                <RiFileCopyLine size={18} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            className={classes.actionButton}
            size="small"
            onClick={dialog.open}
            aria-label="Expand chart"
          >
            <RiFullscreenLine size={18} />
          </IconButton>
        </div>
      </div>

      {dialogOpen && (
        <DialogComposite.Root
          open={dialogOpen}
          onClose={dialog.close}
          width="xl"
        >
          <DialogComposite.Content>
            <DialogComposite.Header>
              <DialogComposite.Title>Chart</DialogComposite.Title>
              <Tooltip title={copyTooltipTitle(dialogCopy.status)}>
                <IconButton
                  className={classes.dialogCopyButton}
                  onClick={() =>
                    dialogCopy.handleCopyToClipboard(dialogCaptureRef.current)
                  }
                  aria-label="Copy chart to clipboard"
                >
                  {dialogCopy.status === 'success' ? (
                    <RiCheckLine size={24} />
                  ) : (
                    <RiFileCopyLine size={24} />
                  )}
                </IconButton>
              </Tooltip>
              <DialogComposite.CloseButton />
            </DialogComposite.Header>
            <DialogComposite.Body className={classes.dialogBody} autoHeight>
              <div
                ref={dialogCaptureRef}
                className={styles.dialogChartViewport}
              >
                <VegaChart
                  content={content}
                  className={classes.dialogChartContainer}
                  fit="container"
                />
              </div>
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      )}
    </>
  );
};
